import * as THREE from 'three';

const MM_TO_UNIT = 0.1; // 1 mm = 0.1 cm (scene units)
const TRANSITION_ZONE_MULTIPLIER = 1.5; // Extend smoothing beyond flare boundary by this factor

/**
 * Build adjacency graph (1-ring neighbors) for a mesh
 * Adapted from rectification helpers for reuse
 *
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @returns {Array<Uint32Array>} Array of neighbor indices for each vertex
 */
export function buildAdjacency(geometry) {
  const idx = geometry.index ? geometry.index.array : null;
  const vcount = geometry.attributes.position.count;
  const nbrs = Array(vcount)
    .fill(null)
    .map(() => new Set());

  if (!idx) {
    // Generate triangles from sequential triplets if no index
    const n = Math.floor(vcount / 3) * 3;
    for (let i = 0; i < n; i += 3) {
      const a = i,
        b = i + 1,
        c = i + 2;
      nbrs[a].add(b).add(c);
      nbrs[b].add(a).add(c);
      nbrs[c].add(a).add(b);
    }
  } else {
    for (let i = 0; i < idx.length; i += 3) {
      const a = idx[i],
        b = idx[i + 1],
        c = idx[i + 2];
      nbrs[a].add(b).add(c);
      nbrs[b].add(a).add(c);
      nbrs[c].add(a).add(b);
    }
  }

  // Pack sets to typed arrays for speed
  return nbrs.map((s) => Uint32Array.from(s));
}

/**
 * Detect mesh boundary vertices (vertices on edges that belong to only one triangle)
 * These vertices should be preserved and not smoothed to maintain clean mesh edges
 *
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @returns {Uint8Array} Array where 1 = boundary vertex, 0 = interior vertex
 */
export function detectMeshBoundaryVertices(geometry) {
  const idx = geometry.index ? geometry.index.array : null;
  const vcount = geometry.attributes.position.count;
  const isBoundary = new Uint8Array(vcount); // 0 = interior, 1 = boundary

  // Map to count edge occurrences: "minVertex_maxVertex" -> count
  const edgeCount = new Map();

  const addEdge = (a, b) => {
    const min = Math.min(a, b);
    const max = Math.max(a, b);
    const key = `${min}_${max}`;
    edgeCount.set(key, (edgeCount.get(key) || 0) + 1);
  };

  if (!idx) {
    // Non-indexed geometry
    const n = Math.floor(vcount / 3) * 3;
    for (let i = 0; i < n; i += 3) {
      const a = i, b = i + 1, c = i + 2;
      addEdge(a, b);
      addEdge(b, c);
      addEdge(c, a);
    }
  } else {
    // Indexed geometry
    for (let i = 0; i < idx.length; i += 3) {
      const a = idx[i], b = idx[i + 1], c = idx[i + 2];
      addEdge(a, b);
      addEdge(b, c);
      addEdge(c, a);
    }
  }

  // Mark vertices that have at least one edge with count = 1 (boundary edge)
  for (const [key, count] of edgeCount) {
    if (count === 1) {
      const [a, b] = key.split('_').map(Number);
      isBoundary[a] = 1;
      isBoundary[b] = 1;
    }
  }

  const boundaryCount = isBoundary.reduce((sum, val) => sum + val, 0);
  console.log(`[flareSmoothingHelpers] Detected ${boundaryCount} mesh boundary vertices`);

  return isBoundary;
}

/**
 * Identify vertices in the boundary zone of a segment (bottom portion)
 * The boundary zone is where we'll apply smoothing to eliminate sharp edges
 *
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {Map<number, number>} segmentMap - Vertex index -> segment index mapping
 * @param {number} segmentIndex - The segment to process
 * @param {number} segmentHeightMm - Height of the segment in mm
 * @param {number} boundaryPercent - What percentage of the segment height is boundary (default 0.15 = 15%)
 * @returns {Set<number>} Set of vertex indices in the boundary zone
 */
export function identifyBoundaryZone(
  geometry,
  segmentMap,
  segmentIndex,
  segmentHeightMm,
  boundaryPercent = 0.15
) {
  const boundaryVertices = new Set();

  if (!segmentMap || segmentHeightMm <= 0) return boundaryVertices;

  // Get all vertices in this segment and find their Y range
  const segmentVertexIndices = [];
  const positions = geometry.attributes.position;

  for (const [vertexIndex, segIdx] of segmentMap.entries()) {
    if (segIdx === segmentIndex) {
      segmentVertexIndices.push(vertexIndex);
    }
  }

  if (segmentVertexIndices.length === 0) return boundaryVertices;

  // Find min/max Y values in the segment (local space)
  let minY = Infinity;
  let maxY = -Infinity;

  for (const vIdx of segmentVertexIndices) {
    const y = positions.getY(vIdx);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  // The boundary zone is at the bottom (minY side) of the segment
  const segmentHeightUnits = segmentHeightMm * MM_TO_UNIT;
  const boundaryHeightUnits = segmentHeightUnits * boundaryPercent;
  const boundaryThreshold = minY + boundaryHeightUnits;

  // Identify vertices in boundary zone
  for (const vIdx of segmentVertexIndices) {
    const y = positions.getY(vIdx);
    if (y <= boundaryThreshold) {
      boundaryVertices.add(vIdx);
    }
  }

  return boundaryVertices;
}

/**
 * Calculate smooth falloff weights for boundary smoothing
 * Returns 0-1 weights where 1 = full smoothing, 0 = no smoothing
 * Uses smooth transition to avoid creating new discontinuities
 *
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {Set<number>} boundaryVertices - Vertices in the boundary zone
 * @param {Map<number, number>} segmentMap - Vertex index -> segment index mapping
 * @param {number} segmentIndex - The segment being processed
 * @param {number} segmentHeightMm - Height of the segment in mm
 * @param {number} falloffPercent - Additional falloff zone as percentage of height (default 0.10 = 10%)
 * @returns {Float32Array} Weight per vertex (0-1)
 */
export function calculateFalloffWeights(
  geometry,
  boundaryVertices,
  segmentMap,
  segmentIndex,
  segmentHeightMm,
  falloffPercent = 0.10
) {
  const vertexCount = geometry.attributes.position.count;
  const weights = new Float32Array(vertexCount);

  if (boundaryVertices.size === 0) return weights;

  const positions = geometry.attributes.position;

  // Find Y range of boundary vertices
  let minY = Infinity;
  let maxY = -Infinity;

  for (const vIdx of boundaryVertices) {
    const y = positions.getY(vIdx);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  }

  // Calculate falloff zone
  const segmentHeightUnits = segmentHeightMm * MM_TO_UNIT;
  const falloffHeightUnits = segmentHeightUnits * falloffPercent;
  const falloffStart = maxY; // Top of boundary zone
  const falloffEnd = falloffStart + falloffHeightUnits;

  // Assign weights
  for (const [vIdx, segIdx] of segmentMap.entries()) {
    if (segIdx !== segmentIndex) continue;

    const y = positions.getY(vIdx);

    if (boundaryVertices.has(vIdx)) {
      // Full weight in boundary zone
      weights[vIdx] = 1.0;
    } else if (y >= falloffStart && y <= falloffEnd) {
      // Smooth falloff using smoothstep
      const t = (y - falloffStart) / (falloffEnd - falloffStart);
      // Smoothstep function: 3t² - 2t³
      const smoothT = t * t * (3.0 - 2.0 * t);
      weights[vIdx] = 1.0 - smoothT; // 1 at falloffStart, 0 at falloffEnd
    }
    // else weight remains 0
  }

  return weights;
}

/**
 * Apply weighted Laplacian smoothing to vertices
 * This smooths the geometry to eliminate sharp edges while preserving overall shape
 *
 * @param {Float32Array} positions - Position array (will be modified in place)
 * @param {Array<Uint32Array>} adjacency - Neighbor graph
 * @param {Float32Array} weights - Per-vertex smoothing weights (0-1)
 * @param {number} iterations - Number of smoothing iterations (default 3)
 * @param {number} alpha - Smoothing strength per iteration (default 0.6)
 * @param {Uint8Array} meshBoundaryVertices - Optional: Array where 1 = mesh boundary vertex to preserve
 */
export function smoothPositions(positions, adjacency, weights, iterations = 3, alpha = 0.6, meshBoundaryVertices = null) {
  const vertexCount = adjacency.length;
  const tmp = new Float32Array(positions.length);

  for (let iter = 0; iter < iterations; iter++) {
    // Copy current positions to temp buffer
    tmp.set(positions);

    for (let i = 0; i < vertexCount; i++) {
      // Skip mesh boundary vertices - they must be preserved
      if (meshBoundaryVertices && meshBoundaryVertices[i]) {
        continue;
      }

      const weight = weights[i];

      // Skip vertices with no smoothing weight
      if (weight <= 0.001) continue;

      const neighbors = adjacency[i];
      if (neighbors.length === 0) continue;

      const i3 = i * 3;
      const origX = positions[i3];
      const origY = positions[i3 + 1];
      const origZ = positions[i3 + 2];

      // Calculate Laplacian (average of neighbors)
      let sumX = 0,
        sumY = 0,
        sumZ = 0;

      for (let j = 0; j < neighbors.length; j++) {
        const nIdx = neighbors[j];
        const n3 = nIdx * 3;
        sumX += positions[n3];
        sumY += positions[n3 + 1];
        sumZ += positions[n3 + 2];
      }

      const inv = 1.0 / neighbors.length;
      const avgX = sumX * inv;
      const avgY = sumY * inv;
      const avgZ = sumZ * inv;

      // Blend toward Laplacian based on weight and alpha
      const blend = alpha * weight;
      tmp[i3] = origX * (1.0 - blend) + avgX * blend;
      tmp[i3 + 1] = origY * (1.0 - blend) + avgY * blend;
      tmp[i3 + 2] = origZ * (1.0 - blend) + avgZ * blend;
    }

    // Copy smoothed positions back
    positions.set(tmp);
  }
}

/**
 * Apply boundary smoothing to a single segment
 *
 * @param {THREE.BufferGeometry} geometry - The mesh geometry (will be modified)
 * @param {Map<number, number>} segmentMap - Vertex index -> segment index mapping
 * @param {number} segmentIndex - The segment to smooth
 * @param {number} segmentHeightMm - Height of the segment in mm
 * @param {Array<Uint32Array>} adjacency - Pre-computed adjacency graph
 * @param {Object} config - Smoothing configuration
 * @param {Uint8Array} meshBoundaryVertices - Optional: Array where 1 = mesh boundary vertex to preserve
 */
export function smoothSegmentBoundary(
  geometry,
  segmentMap,
  segmentIndex,
  segmentHeightMm,
  adjacency,
  config = {},
  meshBoundaryVertices = null
) {
  const {
    boundaryHeightPercent = 0.15,
    falloffPercent = 0.10,
    iterations = 4,
    strength = 0.6,
  } = config;

  // Identify boundary zone
  const boundaryVertices = identifyBoundaryZone(
    geometry,
    segmentMap,
    segmentIndex,
    segmentHeightMm,
    boundaryHeightPercent
  );

  if (boundaryVertices.size === 0) return;

  // Calculate falloff weights
  const weights = calculateFalloffWeights(
    geometry,
    boundaryVertices,
    segmentMap,
    segmentIndex,
    segmentHeightMm,
    falloffPercent
  );

  // Apply smoothing
  const positions = geometry.attributes.position.array;
  smoothPositions(positions, adjacency, weights, iterations, strength, meshBoundaryVertices);
}

/**
 * Apply boundary smoothing to all segments with flares
 * This is the main entry point called from applyFlareToGeometry
 *
 * @param {THREE.BufferGeometry} geometry - The mesh geometry (will be modified)
 * @param {Map<number, number>} segmentMap - Vertex index -> segment index mapping
 * @param {Object} flareState - Flare state from store
 * @param {Array<Uint32Array>} adjacency - Pre-computed adjacency graph (optional, will compute if not provided)
 * @param {Object} config - Smoothing configuration (optional)
 */
export function smoothAllSegmentBoundaries(geometry, segmentMap, flareState, adjacency = null, config = null, meshBoundaryVertices = null) {
  if (!geometry || !segmentMap || segmentMap.size === 0) return;

  // Use provided config or get from flare state or use defaults
  const smoothingConfig = config || flareState.smoothingConfig || {
    enabled: true,
    boundaryHeightPercent: 0.15,
    falloffPercent: 0.10,
    iterations: 4,
    strength: 0.6,
  };

  // Skip if smoothing is disabled
  if (!smoothingConfig.enabled) return;

  // Build adjacency if not provided
  const adj = adjacency || buildAdjacency(geometry);

  // Smooth each segment that has a non-zero height
  const segmentCount = flareState.segmentHeightValues.length;

  for (let segIdx = 0; segIdx < segmentCount; segIdx++) {
    const heightMm = flareState.segmentHeightValues[segIdx];

    // Only smooth segments with actual flare applied
    if (heightMm > 0) {
      smoothSegmentBoundary(geometry, segmentMap, segIdx, heightMm, adj, smoothingConfig, meshBoundaryVertices);
    }
  }

  // Mark geometry as needing update
  geometry.attributes.position.needsUpdate = true;
}

/**
 * Identify vertices near angular boundaries between segments
 * These are vertices where two segments meet at their angular boundaries (left/right edges)
 *
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {Map<number, number>} segmentMap - Vertex index -> segment index mapping
 * @param {Array} flareValues - Segment division percentages [20, 40, 60, 80, 100]
 * @param {THREE.Vector3} centroid - Boundary centroid for angle calculation
 * @param {number} blendAngleDegrees - Blend zone width in degrees (default 5°)
 * @returns {Map} vertexIndex -> {leftSegment, rightSegment, blendWeight, angle}
 */
export function identifyAngularBoundaryVertices(
  geometry,
  segmentMap,
  flareValues,
  centroid,
  blendAngleDegrees = 5,
  options = {}
) {
  const boundaryVertices = new Map();
  if (!geometry || !segmentMap || !flareValues || !centroid) return boundaryVertices;

  const positions = geometry.attributes.position;
  // Convert blend zone width from degrees to percent of full circle
  // e.g., 5° -> ~1.3889% total width, so half = ~0.6944%
  const halfBlendPercent = (blendAngleDegrees / 360) * 50.0; // (deg/360*100)/2

  // Optional distance gating to avoid touching the very top/bottom of the flare band
  const distances = options.distances || null; // Float32Array in scene units
  const segmentHeightsMm = options.segmentHeightsMm || null; // number[] per segment
  const distanceBand = options.distanceBand || [0.15, 0.85]; // normalized nd range to include

  // For each vertex, check if it's near any segment boundary
  for (const [vertexIndex, segmentIndex] of segmentMap.entries()) {
    const vPos = new THREE.Vector3().fromBufferAttribute(positions, vertexIndex);

    // Calculate angle in XZ plane (0° at top/north, clockwise)
    const dx = vPos.x - centroid.x;
    const dz = vPos.z - centroid.z;
    let angle = Math.atan2(dx, -dz) * (180 / Math.PI);
    if (angle < 0) angle += 360;

    // Convert to percentage [0, 100)
    const anglePercent = (angle / 360) * 100;

    // Check distance to each segment boundary
    for (let i = 0; i < flareValues.length; i++) {
      const boundaryPercent = flareValues[i];

      // Calculate angular distance to this boundary
      let angularDist = anglePercent - boundaryPercent;

      // Handle wrap-around at 0°/360°
      if (angularDist > 50) angularDist -= 100;
      if (angularDist < -50) angularDist += 100;

      const absAngularDist = Math.abs(angularDist);

      // Check if within blend zone
      if (absAngularDist <= halfBlendPercent) {
        // Optionally reject vertices too close to the top/bottom of the flare
        if (distances && segmentHeightsMm) {
          const segIdx = segmentIndex; // current vertex's segment
          const hMm = segmentHeightsMm[segIdx] || 0;
          if (hMm > 0) {
            const hUnits = hMm * MM_TO_UNIT;
            const d = distances[vertexIndex];
            if (isFinite(d) && hUnits > 0) {
              const nd = d / hUnits;
              if (!(nd >= distanceBand[0] && nd <= distanceBand[1])) {
                continue; // skip this vertex for angular smoothing
              }
            }
          }
        }
        // This vertex is near a segment boundary
        const leftSegment = i; // Segment on left side of boundary
        const rightSegment = (i + 1) % flareValues.length; // Segment on right side

        // Calculate blend weight: 0 = fully left segment, 1 = fully right segment
        // At exact boundary: blendWeight = 0.5
        const blendWeight = (angularDist + halfBlendPercent) / (2.0 * halfBlendPercent);

        boundaryVertices.set(vertexIndex, {
          leftSegment,
          rightSegment,
          blendWeight: Math.max(0, Math.min(1, blendWeight)),
          angle: anglePercent,
          boundaryAngle: boundaryPercent
        });

        break; // Vertex can only be near one boundary
      }
    }
  }

  return boundaryVertices;
}

/**
 * Smooth angular boundary vertices to eliminate sharp edges between segments
 * Uses weighted Laplacian smoothing based on blend weights
 *
 * @param {THREE.BufferGeometry} geometry - The mesh geometry (will be modified)
 * @param {Map} angularBoundaryData - Angular boundary vertices data
 * @param {Array<Uint32Array>} adjacency - Adjacency graph
 * @param {number} iterations - Number of smoothing iterations (default 4)
 * @param {number} strength - Smoothing strength per iteration (default 0.6)
 * @param {Uint8Array} meshBoundaryVertices - Optional: Array where 1 = mesh boundary vertex to preserve
 */
export function smoothAngularBoundaries(
  geometry,
  angularBoundaryData,
  adjacency,
  iterations = 4,
  strength = 0.6,
  meshBoundaryVertices = null
) {
  if (!geometry || !angularBoundaryData || angularBoundaryData.size === 0 || !adjacency) return;

  const positions = geometry.attributes.position.array;
  const vertexCount = adjacency.length;
  const tmp = new Float32Array(positions.length);

  // Create weight array
  const weights = new Float32Array(vertexCount);
  for (const [vertexIndex, boundaryInfo] of angularBoundaryData.entries()) {
    // Weight based on how close to exact boundary (higher weight = closer to boundary = more smoothing)
    const distFromCenter = Math.abs(boundaryInfo.blendWeight - 0.5);
    weights[vertexIndex] = 1.0 - (distFromCenter * 2.0); // 1.0 at center, 0.0 at edges
  }

  // Apply Laplacian smoothing
  for (let iter = 0; iter < iterations; iter++) {
    tmp.set(positions);

    for (const [vertexIndex, _] of angularBoundaryData.entries()) {
      // Skip mesh boundary vertices - they must be preserved
      if (meshBoundaryVertices && meshBoundaryVertices[vertexIndex]) {
        continue;
      }

      const weight = weights[vertexIndex];
      if (weight <= 0.001) continue;

      const neighbors = adjacency[vertexIndex];
      if (neighbors.length === 0) continue;

      const i3 = vertexIndex * 3;
      const origX = positions[i3];
      const origY = positions[i3 + 1];
      const origZ = positions[i3 + 2];

      // Calculate Laplacian (average of neighbors)
      let sumX = 0, sumY = 0, sumZ = 0;
      for (let j = 0; j < neighbors.length; j++) {
        const nIdx = neighbors[j];
        const n3 = nIdx * 3;
        sumX += positions[n3];
        sumY += positions[n3 + 1];
        sumZ += positions[n3 + 2];
      }

      const inv = 1.0 / neighbors.length;
      const avgX = sumX * inv;
      const avgY = sumY * inv;
      const avgZ = sumZ * inv;

      // Blend toward Laplacian
      const blend = strength * weight;
      tmp[i3] = origX * (1.0 - blend) + avgX * blend;
      tmp[i3 + 1] = origY * (1.0 - blend) + avgY * blend;
      tmp[i3 + 2] = origZ * (1.0 - blend) + avgZ * blend;
    }

    positions.set(tmp);
  }

  geometry.attributes.position.needsUpdate = true;
}

/**
 * Smooth the transition zone between flared and non-flared regions
 * This prevents sharp normal discontinuities at the flare boundary
 *
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {Map<number, number>} segmentMap - Vertex to segment mapping
 * @param {Float32Array} distances - Distance field from boundary
 * @param {Array<number>} segmentHeightValues - Heights for each segment in mm
 * @param {Array<number>} segmentFalloffHeightValues - Falloff heights for each segment in mm
 * @param {Array<Uint32Array>} adjacency - Pre-computed adjacency graph
 * @param {Object} config - Smoothing configuration
 * @param {Uint8Array} meshBoundaryVertices - Optional: Array where 1 = mesh boundary vertex to preserve
 */
export function smoothFlareTransitionZone(
  geometry,
  segmentMap,
  distances,
  segmentHeightValues,
  segmentFalloffHeightValues,
  adjacency,
  config = {},
  meshBoundaryVertices = null
) {
  const {
    iterations = 5,
    strength = 0.7,
    transitionMultiplier = TRANSITION_ZONE_MULTIPLIER
  } = config;

  if (!geometry || !segmentMap || !distances || segmentMap.size === 0) return;

  const positions = geometry.attributes.position.array;
  const vertexCount = positions.length / 3;

  // Calculate max influence distance (including falloff and transition zone)
  const totalHeights = segmentHeightValues.map((h, i) => {
    const falloffH = segmentFalloffHeightValues?.[i] || 0;
    return h + falloffH;
  });
  const maxHeightMm = Math.max(...totalHeights);
  const maxInfluenceDistance = maxHeightMm * MM_TO_UNIT;
  const transitionDistance = maxInfluenceDistance * transitionMultiplier;

  // Identify transition zone vertices
  // These are vertices near the flare boundary (just inside and outside the influence zone)
  const transitionWeights = new Float32Array(vertexCount);

  for (let i = 0; i < vertexCount; i++) {
    const dist = distances[i];

    if (!isFinite(dist)) continue;

    // Calculate transition zone weight
    // Full weight at flare boundary, fading to zero at transition distance
    if (dist >= maxInfluenceDistance && dist <= transitionDistance) {
      // Outside flare but within transition zone
      const normalizedDist = (dist - maxInfluenceDistance) / (transitionDistance - maxInfluenceDistance);
      transitionWeights[i] = 1.0 - normalizedDist; // 1.0 at boundary, 0.0 at transition edge
    } else if (dist < maxInfluenceDistance && dist > maxInfluenceDistance * 0.7) {
      // Inside flare but near boundary (top 30% of flare height)
      const normalizedDist = (maxInfluenceDistance - dist) / (maxInfluenceDistance * 0.3);
      transitionWeights[i] = 1.0 - normalizedDist; // 1.0 at boundary, 0.0 deeper inside
    }
  }

  // Apply Laplacian smoothing to transition zone
  const tmp = new Float32Array(positions);

  for (let iter = 0; iter < iterations; iter++) {
    tmp.set(positions);

    for (let i = 0; i < vertexCount; i++) {
      // Skip mesh boundary vertices - they must be preserved
      if (meshBoundaryVertices && meshBoundaryVertices[i]) {
        continue;
      }

      const weight = transitionWeights[i];
      if (weight <= 0.001) continue;

      const neighbors = adjacency[i];
      if (!neighbors || neighbors.length === 0) continue;

      const i3 = i * 3;
      const origX = positions[i3];
      const origY = positions[i3 + 1];
      const origZ = positions[i3 + 2];

      // Calculate Laplacian (average of neighbors)
      let sumX = 0, sumY = 0, sumZ = 0;
      for (let j = 0; j < neighbors.length; j++) {
        const nIdx = neighbors[j];
        const n3 = nIdx * 3;
        sumX += positions[n3];
        sumY += positions[n3 + 1];
        sumZ += positions[n3 + 2];
      }

      const inv = 1.0 / neighbors.length;
      const avgX = sumX * inv;
      const avgY = sumY * inv;
      const avgZ = sumZ * inv;

      // Blend toward Laplacian based on weight and strength
      const blend = strength * weight;
      tmp[i3] = origX * (1.0 - blend) + avgX * blend;
      tmp[i3 + 1] = origY * (1.0 - blend) + avgY * blend;
      tmp[i3 + 2] = origZ * (1.0 - blend) + avgZ * blend;
    }

    positions.set(tmp);
  }

  geometry.attributes.position.needsUpdate = true;

  // Count affected vertices for logging
  let affectedCount = 0;
  for (let i = 0; i < vertexCount; i++) {
    if (transitionWeights[i] > 0.001) affectedCount++;
  }

  console.log(`[flareSmoothingHelpers] Smoothed ${affectedCount} vertices in transition zone`);
}

/**
 * Smooth boundary curve vertices across segment transitions
 * This ensures the trimline curve remains smooth even when segments have different deflect values
 *
 * @param {THREE.BufferGeometry} geometry - The mesh geometry
 * @param {Array<number>} boundaryVertices - Array of boundary vertex indices
 * @param {Map<number, number>} segmentMap - Vertex index to segment index mapping
 * @param {Array<number>} flareValues - Segment boundary positions [20, 40, 60, 80, 100]
 * @param {THREE.Vector3} centroid - Boundary centroid in local space
 * @param {Array<Uint32Array>} adjacency - Vertex adjacency graph
 * @param {Object} options - Smoothing options
 *   {iterations: number, strength: number, blendZoneDegrees: number}
 */
export function smoothBoundaryCurve(
  geometry,
  boundaryVertices,
  segmentMap,
  flareValues,
  centroid,
  adjacency,
  options = {},
  meshBoundaryVertices = null
) {
  const {
    iterations = 3,
    strength = 0.7,
    blendZoneDegrees = 10 // Smooth boundary vertices within 10° of segment boundaries
  } = options;

  if (boundaryVertices.length === 0) return;

  const positions = geometry.attributes.position;
  const posArray = positions.array;
  const numSegments = flareValues.length;

  // Create a set for fast boundary vertex lookup
  const boundarySet = new Set(boundaryVertices);

  // Calculate angular positions for boundary vertices
  const boundaryAngles = new Map();
  for (const vIdx of boundaryVertices) {
    const x = posArray[vIdx * 3];
    const z = posArray[vIdx * 3 + 2];
    const dx = x - centroid.x;
    const dz = z - centroid.z;
    let angle = Math.atan2(dx, -dz) * (180 / Math.PI);
    if (angle < 0) angle += 360;
    boundaryAngles.set(vIdx, angle);
  }

  // Identify boundary vertices near segment boundaries
  const boundaryTransitionVertices = new Set();
  const segmentBoundaryAngles = flareValues.map(pct => (pct / 100) * 360);

  for (const vIdx of boundaryVertices) {
    // Skip mesh boundary vertices - they must be preserved
    if (meshBoundaryVertices && meshBoundaryVertices[vIdx]) {
      continue;
    }

    const angle = boundaryAngles.get(vIdx);

    // Check distance to nearest segment boundary
    let minDistToBoundary = Infinity;
    for (const boundaryAngle of segmentBoundaryAngles) {
      // Handle wraparound at 0/360
      const dist = Math.min(
        Math.abs(angle - boundaryAngle),
        Math.abs(angle - boundaryAngle + 360),
        Math.abs(angle - boundaryAngle - 360)
      );
      minDistToBoundary = Math.min(minDistToBoundary, dist);
    }

    // If within blend zone degrees of a segment boundary, mark for smoothing
    if (minDistToBoundary <= blendZoneDegrees) {
      boundaryTransitionVertices.add(vIdx);
    }
  }

  if (boundaryTransitionVertices.size === 0) {
    console.log('[flareSmoothingHelpers] No boundary transition vertices found');
    return;
  }

  // Apply smoothing iterations
  const tmp = new Float32Array(posArray.length);

  for (let iter = 0; iter < iterations; iter++) {
    tmp.set(posArray);

    for (const vIdx of boundaryTransitionVertices) {
      const neighbors = adjacency[vIdx];
      if (!neighbors || neighbors.length === 0) continue;

      // Only average with neighboring boundary vertices
      const boundaryNeighbors = [];
      for (const nIdx of neighbors) {
        if (boundarySet.has(nIdx)) {
          boundaryNeighbors.push(nIdx);
        }
      }

      if (boundaryNeighbors.length === 0) continue;

      // Calculate average position of boundary neighbors
      let avgX = 0, avgY = 0, avgZ = 0;
      for (const nIdx of boundaryNeighbors) {
        avgX += posArray[nIdx * 3];
        avgY += posArray[nIdx * 3 + 1];
        avgZ += posArray[nIdx * 3 + 2];
      }
      avgX /= boundaryNeighbors.length;
      avgY /= boundaryNeighbors.length;
      avgZ /= boundaryNeighbors.length;

      // Blend original position with average
      const i3 = vIdx * 3;
      const origX = posArray[i3];
      const origY = posArray[i3 + 1];
      const origZ = posArray[i3 + 2];

      tmp[i3] = origX * (1.0 - strength) + avgX * strength;
      tmp[i3 + 1] = origY * (1.0 - strength) + avgY * strength;
      tmp[i3 + 2] = origZ * (1.0 - strength) + avgZ * strength;
    }

    positions.set(tmp);
  }

  positions.needsUpdate = true;
  console.log(`[flareSmoothingHelpers] Smoothed ${boundaryTransitionVertices.size} boundary vertices across segment transitions`);
}
